import React, { useState, useEffect } from 'react';
import '../style/Comparison.css';
import Select from 'react-select';
import { Box, Typography, Button } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import { Link } from 'react-router-dom';
import { apiCall } from '../services/apiService';

function Comparison() {
  const [allProducts, setAllProducts] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState(() => {
    // Initialize from localStorage if available
    const saved = localStorage.getItem('comparisonSelectedProducts');
    return saved ? JSON.parse(saved) : [];
  });
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch list of all products for selection
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await apiCall('api/products');

        if (!response.ok) {
          throw new Error('Failed to fetch products');
        }

        const data = await response.json();
        setAllProducts(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching products list:', err);
        setError('Failed to load products');
        setAllProducts([]); // Set empty array to prevent map error
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Save selected products to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('comparisonSelectedProducts', JSON.stringify(selectedProductIds));
  }, [selectedProductIds]);

  // Fetch detailed data for selected products
  useEffect(() => {
    const fetchSelectedProducts = async () => {
      if (selectedProductIds.length > 0) {
        try {
          const fetchPromises = selectedProductIds.map(async (id) => {
            const response = await apiCall(`api/product/${id}`);

            if (!response.ok) {
              throw new Error(`Failed to fetch product ${id}`);
            }

            return response.json();
          });

          const products = await Promise.all(fetchPromises);
          setSelectedProducts(products);
        } catch (err) {
          console.error('Error fetching selected products:', err);
          setError('Failed to load product details');
        }
      } else {
        setSelectedProducts([]);
      }
    };

    fetchSelectedProducts();
  }, [selectedProductIds]);

  // Product options for dropdown, sorted alphabetically
  const productOptions = (allProducts || [])
    .map(prod => ({
      value: prod.id,
      label: `${prod.brand} - ${prod.line} - ${prod.name}`
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Handle product selection (up to 3 products)
  const handleProductSelect = (selectedOptions) => {
    if (selectedOptions && selectedOptions.length <= 5) {
      const selectedIds = selectedOptions.map(option => option.value);
      setSelectedProductIds(selectedIds);
    }
  };

  // Clear all selections
  const handleClearAll = () => {
    setSelectedProductIds([]);
    setSelectedProducts([]);
    localStorage.removeItem('comparisonSelectedProducts');
  };

  // Helper function to format nutrient values (same as Product page)
  const getNutrientSource = (product, fieldKey) => product?.nutrientSources?.[fieldKey] || null;

  const formatNutrientValue = (value, source) => {
    if (value === null || value === undefined) return "N/A";
    if (value === 0) return "0%";
    const suffix = source === 'guaranteed_analysis' ? '*' : '';
    return `${parseFloat(value).toFixed(2)}%${suffix}`;
  };

  // Format ingredients string (same as Product page)
  const formatIngredients = (ingredients) => {
    if (ingredients && typeof ingredients === 'string') {
      return ingredients
        .replace(/[{}"]/g, '')
        .split(',')
        .join(', ');
    }
    return 'Information not available';
  };

  // Format array fields like proteins and lifestage
  const formatArrayField = (field) => {
    if (Array.isArray(field) && field.length > 0) {
      return field.join(', ');
    }
    return 'N/A';
  };

  if (loading) {
    return (
      <div className="comparison-container">
        <div className="page-header">
          <h1 className="page-title">Comparison</h1>
        </div>
        <Skeleton variant="rectangular" width="100%" height={60} style={{ marginTop: 20 }} />
        <Skeleton variant="rectangular" width="100%" height={400} style={{ marginTop: 20 }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="comparison-container">
        <div className="page-header">
          <h1 className="page-title">Comparison</h1>
        </div>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="comparison-container">
      <div className="page-header">
        <h1 className="page-title">Comparison</h1>
      </div>

      <p className="comparison-description">
        Select up to 5 different products to compare their nutritional profiles and features.
      </p>

      {/* Product Selection */}
      <div className="product-selection-section">
        <Select
          className="product-selection"
          options={productOptions}
          onChange={handleProductSelect}
          value={productOptions.filter(option => selectedProductIds.includes(option.value))}
          placeholder="No product selected"
          styles={{ placeholder: (base) => ({ ...base, fontSize: '1rem' }) }}
          isSearchable
          isMulti
          closeMenuOnSelect={false}
          maxMenuHeight={300}
        />
        {selectedProductIds.length > 0 && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Button
              variant="outlined"
              onClick={handleClearAll}
              size="medium"
            >
              Clear All
            </Button>
          </Box>
        )}
      </div>

      {/* Comparison Table */}
      {selectedProducts.length > 0 && (
        <div className="comparison-section">
          <table className="comparison-table">
            <thead>
              <tr>
                <th style={{ width: '200px' }}>Product</th>
                {selectedProducts.map(product => (
                  <th key={product.id}>
                    <Link
                      to={`/Product/${product.id}`}
                      style={{
                        color: '#1976d2',
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {product.brand} - {product.line} - {product.name}
                    </Link>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Product Images */}
              <tr>
                <td><strong>Image</strong></td>
                {selectedProducts.map(product => (
                  <td key={product.id}>
                    <img
                      src={product.cloudinaryUrl || '/product-placeholder.png'}
                      alt={`${product.brand} - ${product.line} - ${product.name}`}
                      style={{
                        width: product.cloudinaryUrl ? '150px' : '100px',
                        height: 'auto'
                      }}
                      onError={(e) => {
                        e.target.src = '/product-placeholder.png';
                        e.target.style.width = '100px';
                      }}
                    />
                  </td>
                ))}
              </tr>

              {/* Basic Information */}
              <tr>
                <td><strong>Category</strong></td>
                {selectedProducts.map(product => (
                  <td key={product.id}>{product.category}</td>
                ))}
              </tr>

              <tr>
                <td><strong>Texture</strong></td>
                {selectedProducts.map(product => (
                  <td key={product.id}>{product.texture || 'N/A'}</td>
                ))}
              </tr>

              <tr>
                <td><strong>Main Proteins</strong></td>
                {selectedProducts.map(product => (
                  <td key={product.id}>{formatArrayField(product.proteinsDetailed)}</td>
                ))}
              </tr>

              <tr>
                <td><strong>Format</strong></td>
                {selectedProducts.map(product => (
                  <td key={product.id}>{formatArrayField(product.format)}</td>
                ))}
              </tr>

              <tr>
                <td><strong>Exists in</strong></td>
                {selectedProducts.map(product => (
                  <td key={product.id}>
                    {product.variants && product.variants.length > 0
                      ? product.variants.map((variant, index) => (
                        <span key={variant.id}>
                          {variant.sizeName}{variant.format ? ` ${variant.format.toLowerCase()}` : ''}
                          {index < product.variants.length - 1 ? ', ' : ''}
                        </span>
                      ))
                      : 'N/A'}
                  </td>
                ))}
              </tr>

              {/* Features and Special Properties */}
              <tr>
                <td><strong>Meets AAFCO</strong></td>
                {selectedProducts.map(product => (
                  <td key={product.id}>{product.isAafco ? '✓' : '✗'}</td>
                ))}
              </tr>

              <tr>
                <td><strong>Grain-Free</strong></td>
                {selectedProducts.map(product => (
                  <td key={product.id}>{product.isGrainFree ? '✓' : '✗'}</td>
                ))}
              </tr>

              <tr>
                <td><strong>GMO-free</strong></td>
                {selectedProducts.map(product => (
                  <td key={product.id}>{product.isGmoFree ? '✓' : '✗'}</td>
                ))}
              </tr>

              <tr>
                <td><strong>Limited Ingredients</strong></td>
                {selectedProducts.map(product => (
                  <td key={product.id}>{product.isLid ? '✓' : '✗'}</td>
                ))}
              </tr>

              <tr>
                <td><strong>Raw food</strong></td>
                {selectedProducts.map(product => (
                  <td key={product.id}>{product.containsRawFood ? '✓' : '✗'}</td>
                ))}
              </tr>

              <tr>
                <td><strong>Prescription</strong></td>
                {selectedProducts.map(product => (
                  <td key={product.id}>{product.isPrescription ? '✓' : '✗'}</td>
                ))}
              </tr>

              <tr>
                <td><strong>Calories content</strong></td>
                {selectedProducts.map(product => (
                  <td key={product.id}>
                    {product.caloriesPerKg
                      ? parseInt(product.caloriesPerKg) + ' kcal/kg'
                      : 'N/A'}
                  </td>
                ))}
              </tr>

              {/* Nutritional Information */}
              <tr>
                <td><strong>Protein</strong></td>
                {selectedProducts.map(product => (
                  <td key={product.id} style={{
                    color: getNutrientSource(product, 'proteinDryMatter') === 'guaranteed_analysis'
                      ? 'blue'
                      : 'inherit'
                  }}>
                    {formatNutrientValue(
                      product.proteinDryMatter,
                      getNutrientSource(product, 'proteinDryMatter')
                    )}
                  </td>
                ))}
              </tr>

              <tr>
                <td><strong>Phosphorus</strong></td>
                {selectedProducts.map(product => (
                  <td key={product.id} style={{
                    color: getNutrientSource(product, 'phosphorusDryMatter') === 'guaranteed_analysis'
                      ? 'blue'
                      : 'inherit'
                  }}>
                    {formatNutrientValue(
                      product.phosphorusDryMatter,
                      getNutrientSource(product, 'phosphorusDryMatter')
                    )}
                  </td>
                ))}
              </tr>

              <tr>
                <td><strong>Carbs</strong></td>
                {selectedProducts.map(product => (
                  <td key={product.id} style={{
                    color: getNutrientSource(product, 'carbsDryMatter') === 'guaranteed_analysis'
                      ? 'blue'
                      : 'inherit'
                  }}>
                    {formatNutrientValue(
                      product.carbsDryMatter,
                      getNutrientSource(product, 'carbsDryMatter')
                    )}
                  </td>
                ))}
              </tr>

              <tr>
                <td><strong>Sodium</strong></td>
                {selectedProducts.map(product => (
                  <td key={product.id} style={{
                    color: getNutrientSource(product, 'sodiumDryMatter') === 'guaranteed_analysis'
                      ? 'blue'
                      : 'inherit'
                  }}>
                    {formatNutrientValue(
                      product.sodiumDryMatter,
                      getNutrientSource(product, 'sodiumDryMatter')
                    )}
                  </td>
                ))}
              </tr>

              <tr>
                <td><strong>Fiber</strong></td>
                {selectedProducts.map(product => (
                  <td key={product.id} style={{
                    color: getNutrientSource(product, 'fiberDryMatter') === 'guaranteed_analysis'
                      ? 'blue'
                      : 'inherit'
                  }}>
                    {formatNutrientValue(
                      product.fiberDryMatter,
                      getNutrientSource(product, 'fiberDryMatter')
                    )}
                  </td>
                ))}
              </tr>

              <tr>
                <td><strong>Fat</strong></td>
                {selectedProducts.map(product => (
                  <td key={product.id} style={{
                    color: getNutrientSource(product, 'fatDryMatter') === 'guaranteed_analysis'
                      ? 'blue'
                      : 'inherit'
                  }}>
                    {formatNutrientValue(
                      product.fatDryMatter,
                      getNutrientSource(product, 'fatDryMatter')
                    )}
                  </td>
                ))}
              </tr>

              <tr>
                <td><strong>Potassium</strong></td>
                {selectedProducts.map(product => (
                  <td key={product.id} style={{
                    color: getNutrientSource(product, 'potassiumDryMatter') === 'guaranteed_analysis'
                      ? 'blue'
                      : 'inherit'
                  }}>
                    {formatNutrientValue(
                      product.potassiumDryMatter,
                      getNutrientSource(product, 'potassiumDryMatter')
                    )}
                  </td>
                ))}
              </tr>

              <tr>
                <td><strong>Ash</strong></td>
                {selectedProducts.map(product => (
                  <td key={product.id} style={{
                    color: getNutrientSource(product, 'ashDryMatter') === 'guaranteed_analysis'
                      ? 'blue'
                      : 'inherit'
                  }}>
                    {formatNutrientValue(
                      product.ashDryMatter,
                      getNutrientSource(product, 'ashDryMatter')
                    )}
                  </td>
                ))}
              </tr>

            </tbody>
          </table>

          {/* Legend */}
          <Box sx={{
            mt: 3,
            mb: 3,
            mx: 1.5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.2
          }}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontStyle: 'italic',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                textAlign: 'center'
              }}
            >
              <span style={{ color: 'blue' }}>*</span>
              Nutrient values displayed in blue indicate that the data come from guaranteed analysis and is not fully accurate
            </Typography>
          </Box>
        </div>
      )}

      {selectedProductIds.length === 0 && (
        <div className="no-selection-message">
          <Typography variant="body1" color="text.secondary" textAlign="center">
            No product selected.
          </Typography>
        </div>
      )}
    </div>
  );
}

export default Comparison;
